import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import axios from "axios";

const AccountSettingsContext = createContext(null);

export const useAccountSettings = () => {
  const context = useContext(AccountSettingsContext);
  if (!context) {
    throw new Error("useAccountSettings must be used within AccountSettingsProvider");
  }
  return context;
};

export const AccountSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tenantId, setTenantId] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const fetchedKeyRef = useRef(null);
  const loadingKeyRef = useRef(null);
  const settingsRef = useRef(null);

  const updateSettings = useCallback((nextSettings) => {
    settingsRef.current = nextSettings;
    setSettings(nextSettings);
  }, []);

  const requestSettings = useCallback(async (currentTenantId, currentCompanyId, forceRefresh = false) => {
    if (!currentTenantId || !currentCompanyId) {
      return;
    }

    const requestKey = `${currentTenantId}:${currentCompanyId}`;
    if (!forceRefresh) {
      if (loadingKeyRef.current === requestKey) return;
      if (fetchedKeyRef.current === requestKey && settingsRef.current) return;
    }

    loadingKeyRef.current = requestKey;
    setLoading(true);
    setTenantId(currentTenantId);
    setCompanyId(currentCompanyId);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "https://api.absat-erp.com/acc/setting/get-all",
        {
          tenant_id: currentTenantId,
          company_id: currentCompanyId,
        },
        {
          headers: token
            ? {
                "Content-Type": "application/json; charset=utf-8",
                Authorization: `Bearer ${token}`,
              }
            : {
                "Content-Type": "application/json; charset=utf-8",
              },
        }
      );
      updateSettings(response?.data?.data || null);
      fetchedKeyRef.current = requestKey;
    } catch (error) {
    } finally {
      loadingKeyRef.current = null;
      setLoading(false);
    }
  }, [updateSettings]);

  const fetchSettings = useCallback((currentTenantId, currentCompanyId) => {
    requestSettings(currentTenantId, currentCompanyId, false);
  }, [requestSettings]);

  const refreshSettings = useCallback(() => {
    if (tenantId && companyId) {
      requestSettings(tenantId, companyId, true);
      return;
    }
    fetchedKeyRef.current = null;
    if (tenantId && companyId) {
      requestSettings(tenantId, companyId, true);
    } else {
      updateSettings(null);
    }
  }, [companyId, requestSettings, tenantId, updateSettings]);

  const getChartAccountLevelLimit = () => {
    return parseInt(settings?.chart_account_level_limit) || 3;
  };

  const getCostCenterLevelLimit = () => {
    return parseInt(settings?.cost_center_level_limit) || 3;
  };

  return (
    <AccountSettingsContext.Provider
      value={{
        settings,
        loading,
        fetchSettings,
        refreshSettings,
        getChartAccountLevelLimit,
        getCostCenterLevelLimit,
      }}
    >
      {children}
    </AccountSettingsContext.Provider>
  );
};
